import { useForm, useWatch } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Tooltip,
  Text,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { AlertCircleIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { djs } from '@duke-hq/djs';
import { WorkSchedule, CourseSegment } from '@duke-hq/shared-constants/enrolments';
import { useMemo } from 'react';
import { useTenantContext } from '@duke-hq/app-provider-web/tenant/TenantContext';
import {
  internalEnrolmentState,
  InternalEnrolmentEnrolmentSettingsType,
} from '@duke-hq/app-provider-web/tenant/tenantState/InternalEnrolmentState';
import { ConsultantBanner } from '@duke-hq/app-provider-web/tenant/components/InternalEnrolmentForms/ConsultantBanner';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { getCourseDatesFromAdminCourseSchedule } from '@duke-hq/lib/dates';

export function SkillSetSettings({
  goToNextStep,
  goToPrevStep,
  setStep,
}: {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
}) {
  const tenantCtx = useTenantContext();
  const snap = useSnapshot(internalEnrolmentState);
  const { selectedUserAttributes } = snap;
  const { data: course, isLoading } = trpcTenant.courses.courseSchedule.useQuery(
    snap.selectedCourseAttributes.id,
  );

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<InternalEnrolmentEnrolmentSettingsType>({
    defaultValues: {
      ...snap.enrolmentSettingsData,
      start_at_date: djs(snap.enrolmentSettingsData.start_at_date).isValid()
        ? djs(snap.enrolmentSettingsData.start_at_date).format('YYYY-MM-DD')
        : djs().format('YYYY-MM-DD'),
    },
  });

  const today = djs();
  const [work_schedule, start_at_date] = useWatch({
    control,
    name: ['work_schedule', 'start_at_date'],
  });

  const isFullTime = work_schedule === WorkSchedule.FULL_TIME;

  const courseStartAndEndDates = useMemo(() => {
    if (!course || isLoading) {
      return null;
    }

    try {
      return getCourseDatesFromAdminCourseSchedule({
        adminCourseScheduleItems: course.schedule,
        courseSegment: CourseSegment.FULL_COURSE, // skill sets always use full course segment
        workSchedule: work_schedule,
        startAt: djs(start_at_date),
      });
    } catch {
      // handle bad user input gracefully
      return null;
    }
  }, [course, isLoading, start_at_date, work_schedule]);

  function onSubmit(values: InternalEnrolmentEnrolmentSettingsType) {
    snap.setEnrolmentSettingsData(values);
    goToNextStep();
  }

  return (
    <Box
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      style={{ width: '100%', height: '100%' }}
      noValidate
    >
      <FormMainContainer>
        <FormContentContainer>
          <ConsultantBanner
            firstName={selectedUserAttributes.first_name}
            lastName={selectedUserAttributes.last_name}
            email={selectedUserAttributes.email}
            role={selectedUserAttributes.role}
            canChangeConsultant={false}
            onChangeClick={() => setStep(0)}
          />
          <HStack w="full" justify="space-between" align="start" gap="10px">
            <FormControl isInvalid={!!errors.start_at_date} isRequired>
              <FormLabel htmlFor="start_at_date">
                <Trans>Start date</Trans>
              </FormLabel>
              <Input
                id="start_at_date"
                min={djs(today).format('YYYY-MM-DD')}
                {...register('start_at_date', {
                  required: t`Start date is required.`,
                  min: {
                    value: djs(today).format('YYYY-MM-DD'),
                    message: t`Please enter a valid start date. The date should not be in the past.`,
                  },
                  validate: (value) => djs(value).isValid() || t`Please enter a valid date.`,
                })}
                type="date"
              />
              <FormErrorMessage>{errors.start_at_date?.message}</FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={!!errors.external_id}
              isRequired={tenantCtx?.settings.is_external_id_field_mandatory}
            >
              <FormLabel htmlFor="external_id">
                <Trans>Purchase order</Trans>
              </FormLabel>
              <Input
                id="external_id"
                {...register('external_id', {
                  required:
                    tenantCtx?.settings.is_external_id_field_mandatory === true
                      ? t`This field is required.`
                      : false,
                  setValueAs: (value: string) => {
                    if (typeof value === 'string') {
                      const trimmed = value.trim();
                      return trimmed === '' ? undefined : trimmed;
                    }
                    return value;
                  },
                })}
                type="text"
                spellCheck="false"
                autoComplete="off"
              />
              <FormErrorMessage>{errors.external_id?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <HStack w="full" justify="space-between" align="start" gap="10px">
            <FormControl isRequired>
              <FormLabel>
                <Trans>Enrolment pace</Trans>
              </FormLabel>
              <HStack align="center">
                <ButtonGroup spacing={0} isAttached>
                  <Button
                    colorScheme={isFullTime ? 'brand' : 'gray'}
                    onClick={() => setValue('work_schedule', WorkSchedule.FULL_TIME)}
                    variant={isFullTime ? 'solid' : 'ghost'}
                  >
                    <Trans>Full-time</Trans>
                  </Button>
                  <Button
                    colorScheme={!isFullTime ? 'brand' : 'gray'}
                    onClick={() => setValue('work_schedule', WorkSchedule.PART_TIME)}
                    variant={!isFullTime ? 'solid' : 'ghost'}
                  >
                    <Trans>Part-time</Trans>
                  </Button>
                </ButtonGroup>
                {!isFullTime && (
                  <Tooltip
                    label={
                      <Box
                        bg="bg.surface"
                        borderRadius="8px"
                        color="fg.default"
                        borderWidth="1px"
                        borderColor="border.default"
                        p="8px"
                        fontSize="sm"
                      >
                        <Trans>
                          Please note, by selecting part-time no outcome can be claimed.
                        </Trans>
                      </Box>
                    }
                    fontSize="md"
                    colorScheme="warning"
                    placement="bottom"
                    gutter={16}
                    bg="transparent"
                    shadow="none"
                  >
                    <Icon as={AlertCircleIcon} color="warning.emphasis" boxSize="20px" />
                  </Tooltip>
                )}
              </HStack>
            </FormControl>
          </HStack>
          {courseStartAndEndDates?.courseEndDate ? (
            <Text
              color="fg.emphasis"
              textStyle="sm"
              textAlign="left"
              fontWeight="normal"
              w="full"
              mt={4}
            >
              <Trans>Course is expected to end by</Trans>{' '}
              {courseStartAndEndDates?.courseEndDate.long}
            </Text>
          ) : null}
        </FormContentContainer>
        <FormFooterContainer>
          <Button rightIcon={<ChevronRightIcon />} isLoading={isSubmitting} type="submit">
            <Trans>Next</Trans>
          </Button>
          <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
            <Trans>Back</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    </Box>
  );
}
